import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo, useRef, useState } from 'react';

import { OrderTypeEnum } from '@flipster/apis/types';
import { FormattedMessage } from '@flipster/i18n';
import { ConditionalOrderTypeEnum, ordersBunja } from '@flipster/r230522/original/order';
import { OpenPosition, openPositionsBunja } from '@flipster/r230522/original/position';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import { UsdtPrecision } from '@flipster/utils/constants/currency';
import Num, { numberFormatter } from '@flipster/utils/num';
import segment, { ChartEvent } from '@flipster/utils/segment';

import { IOrderLineAdapter } from '../../../../lib/charting_library/charting_library';
import { ChartStyles } from '../../../misc/constants';
import { useTradingViewDataStorage } from '../../../misc/data-manager';
import { symbolNameToInfo } from '../../../misc/helper';
import TradingView, { TvEventHandler } from '../../../trading-view';
import { useTvHeaderContext } from '../../index';
import { buildTpSlLabel, getTpSlExecutionLabel } from '../tp-sl-label';
import DraggableOrderLineHelper, {
  type DraggableOrderLineCallbacks,
  type OrderLike,
} from './line-helpers/draggable-order-line-helper';
import { resolveTpSlCancelClick } from './line-helpers/draggable-tpsl-line-helper/tp-sl-drag-drop';

/** 드래그 가능한 TP/SL 라인 본문 앞에 붙는 메뉴(세 줄) 마크.
 *  U+2261(수학 'identical to')은 수학 축에 정렬돼 텍스트와 세로 중앙이 더 잘 맞는다. */
const TPSL_MENU_ICON = '≡';

const LineStyles = {
  TriggerLong: { bodyColor: ChartStyles.Color.TriggerLong, lineColor: ChartStyles.Color.Up },
  TriggerShort: { bodyColor: ChartStyles.Color.TriggerShort, lineColor: ChartStyles.Color.Down },
  TP: { bodyColor: ChartStyles.Color.TriggerLong, lineColor: ChartStyles.Color.Up },
  SL: { bodyColor: ChartStyles.Color.TriggerShort, lineColor: ChartStyles.Color.Down },
};

interface DraggableOrdersSectionProps {
  callbacks: DraggableOrderLineCallbacks;
  /** webview면 ×(삭제) 시 web 확인 다이얼로그 없이 곧바로 브리지 전송(native가 확인). 데스크톱(기본)은 web 다이얼로그. */
  isWebview?: boolean;
}

// --- Trigger Orders ---

export const DraggableTriggerOrdersPlugin = ({ callbacks }: DraggableOrdersSectionProps) => {
  const tv = useTvHeaderContext();
  const helperRef = useRef<DraggableOrderLineHelper>(null);
  const [checked, setChecked] = useTradingViewDataStorage({ key: 'triggerOrderVisible' });
  const [symbol, setSymbol] = useState('');

  const { pendingPositionsAtom } = useBunja(ordersBunja);
  const pendingPositions = useAtomValue(pendingPositionsAtom);

  useEffect(() => {
    helperRef.current?.clean();
    helperRef.current = new DraggableOrderLineHelper(
      tv,
      (side) => (side === 'Long' ? LineStyles.TriggerLong : LineStyles.TriggerShort),
      callbacks,
    );
    const handler: TvEventHandler<'onFirstDataLoaded'> = ({ symbol: newSymbol, symbolChanged }) => {
      if (!symbolChanged) return;
      helperRef.current?.clean();
      setSymbol(newSymbol);
    };
    return tv.on('onFirstDataLoaded', handler);
  }, [tv, callbacks]);

  const { symbol: symbolWithoutPriceType } = symbolNameToInfo(symbol);
  const triggerOrders = pendingPositions?.[symbolWithoutPriceType]?.filter(
    (p) => p.orderType !== OrderTypeEnum['Limit'] && p.conditionalOrderType === ConditionalOrderTypeEnum.Invalid,
  );

  useEffect(() => {
    if (!checked.value || !triggerOrders?.length) {
      helperRef.current?.clean();
      return;
    }
    const orders: OrderLike[] = triggerOrders
      .filter((o) => !o.triggerTimestamp)
      .map((o) => ({
        id: o.orderId,
        side: o.side,
        label: 'Trigger',
        qty: numberFormatter(o.qty),
        price: Num(o.triggerPrice).toNumber(),
        slot: o.slot,
      }));
    helperRef.current?.draw(symbol, orders);
  }, [checked.value, symbol, triggerOrders]);

  useEffect(() => () => helperRef.current?.clean(), []);

  return (
    <Checkbox
      checked={checked.value}
      onChange={(e) => {
        setChecked(e.currentTarget.checked);
        segment.track(ChartEvent.ChartEventToggleClick, {
          event_name: 'Trigger orders',
          action: e.currentTarget.checked ? 'turn_on' : 'turn_off',
        });
      }}
    >
      <FormattedMessage id='Trade.r260616.Trigger_orders' />
    </Checkbox>
  );
};

// --- TP/SL ---
// Uses real orderId from OpenPosition.tpOrders/slOrders (multi TP/SL support)

/** TP/SL 라인 ×(삭제) 시 확인 다이얼로그를 띄우고, Confirm일 때만 실제 삭제를 실행한다. */
function confirmRemoveTpSl(tpslType: 'tp' | 'sl', doRemove: () => Promise<void>): Promise<void> {
  return new Promise<void>((resolve) => {
    dialog.open({
      title: {
        label: (
          <FormattedMessage
            id={tpslType === 'tp' ? 'Position.r250401.Remove_tp_title' : 'Position.r250401.Remove_sl_title'}
          />
        ),
      },
      content: (
        <FormattedMessage
          id={tpslType === 'tp' ? 'Position.r250401.Remove_tp_description' : 'Position.r250401.Remove_sl_description'}
        />
      ),
      buttons: {
        primary: (
          <Dialog.Button
            hierarchy='primary'
            size='large'
            label={<FormattedMessage id='Common.r250401.Confirm' />}
            onClick={async () => {
              await doRemove();
              dialog.close();
              resolve();
            }}
          />
        ),
        secondary: (
          <Dialog.Button
            hierarchy='secondary'
            color='neutral'
            size='large'
            label={<FormattedMessage id='Common.r250401.Cancel' />}
            onClick={() => {
              dialog.close();
              resolve(); // 취소 — 삭제하지 않음(라인 유지)
            }}
          />
        ),
      },
    });
  });
}

export const DraggableTpSlPlugin = ({ callbacks, isWebview = false }: DraggableOrdersSectionProps) => {
  const tv = useTvHeaderContext();
  const tpHelperRef = useRef<DraggableOrderLineHelper>(null);
  const slHelperRef = useRef<DraggableOrderLineHelper>(null);
  const [checked, setChecked] = useTradingViewDataStorage({ key: 'TPSLVisible' });
  const [symbol, setSymbol] = useState('');

  const { openPositionsAtom } = useBunja(openPositionsBunja);
  const openPositions = useAtomValue(openPositionsAtom);

  // ×(삭제): 데스크톱은 web 확인 다이얼로그를 거치고, webview는 곧바로 브리지(DELETE_TPSL) → native가 확인.
  const tpCallbacks = useMemo<DraggableOrderLineCallbacks>(
    () => ({
      ...callbacks,
      onCancelClick: resolveTpSlCancelClick({
        isWebview,
        tpslType: 'tp',
        onCancelClick: callbacks.onCancelClick,
        confirm: confirmRemoveTpSl,
      }),
    }),
    [callbacks, isWebview],
  );
  const slCallbacks = useMemo<DraggableOrderLineCallbacks>(
    () => ({
      ...callbacks,
      onCancelClick: resolveTpSlCancelClick({
        isWebview,
        tpslType: 'sl',
        onCancelClick: callbacks.onCancelClick,
        confirm: confirmRemoveTpSl,
      }),
    }),
    [callbacks, isWebview],
  );

  useEffect(() => {
    tpHelperRef.current?.clean();
    slHelperRef.current?.clean();
    tpHelperRef.current = new DraggableOrderLineHelper(tv, () => LineStyles.TP, tpCallbacks, isWebview);
    slHelperRef.current = new DraggableOrderLineHelper(tv, () => LineStyles.SL, slCallbacks, isWebview);
    const handler: TvEventHandler<'onFirstDataLoaded'> = ({ symbol: newSymbol, symbolChanged }) => {
      if (!symbolChanged) return;
      tpHelperRef.current?.clean();
      slHelperRef.current?.clean();
      setSymbol(newSymbol);
    };
    return tv.on('onFirstDataLoaded', handler);
  }, [tv, tpCallbacks, slCallbacks, isWebview]);

  const { symbol: symbolWithoutPriceType } = symbolNameToInfo(symbol);
  const symbolPositions = openPositions?.[symbolWithoutPriceType];

  useEffect(() => {
    if (!checked.value || !symbolPositions?.length) {
      tpHelperRef.current?.clean();
      slHelperRef.current?.clean();
      return;
    }
    // Collect TP/SL orders from all positions, using real orderId
    // ≡(드래그 가능 표시)는 데스크톱에서만. webview는 드래그 scope out이라 prefix 없음.
    const menuIconPrefix = isWebview ? '' : `${TPSL_MENU_ICON} `;
    const tpOrders: OrderLike[] = [];
    const slOrders: OrderLike[] = [];
    for (const position of symbolPositions) {
      for (const order of position.tpOrders) {
        const price = Number(order.triggerPrice || 0);
        if (price === 0) continue;
        const isEntire = Num(order?.qty || 0).isZero();
        const qty = isEntire ? Num(position?.quantity || 0).abs() : Num(order?.qty || 0).abs();
        tpOrders.push({
          id: order.orderId,
          side: 'Long',
          label: `${menuIconPrefix}${buildTpSlLabel({
            type: 'TP',
            triggerPrice: numberFormatter(String(order.triggerPrice ?? 0)),
            executionType: getTpSlExecutionLabel(order.orderType),
            variant: 'short',
          })}`,
          qty: numberFormatter(qty),
          price,
          slot: order.slot,
          pnlContext: {
            positionId: position.positionId,
            lineType: 'TP',
            positionSide: position.side,
            entryPrice: Number(position.avgEntryPrice || 0),
            quantity: qty.toNumber(),
            liqPrice: Number(position.liquidationPrice || 0),
            marginType: position.marginType,
          },
        });
      }
      for (const order of position.slOrders) {
        const price = Number(order.triggerPrice || 0);
        if (price === 0) continue;
        const isEntire = Num(order?.qty || 0).isZero();
        const qty = isEntire ? Num(position?.quantity || 0).abs() : Num(order?.qty || 0).abs();
        slOrders.push({
          id: order.orderId,
          side: 'Short',
          label: `${menuIconPrefix}${buildTpSlLabel({
            type: 'SL',
            triggerPrice: numberFormatter(String(order.triggerPrice ?? 0)),
            executionType: getTpSlExecutionLabel(order.orderType),
            variant: 'short',
          })}`,
          qty: numberFormatter(qty),
          price,
          slot: order.slot,
          pnlContext: {
            positionId: position.positionId,
            lineType: 'SL',
            positionSide: position.side,
            entryPrice: Number(position.avgEntryPrice || 0),
            quantity: qty.toNumber(),
            liqPrice: Number(position.liquidationPrice || 0),
            marginType: position.marginType,
          },
        });
      }
    }
    tpHelperRef.current?.draw(symbol, tpOrders);
    slHelperRef.current?.draw(symbol, slOrders);
  }, [checked.value, symbol, symbolPositions, isWebview]);

  useEffect(() => {
    return () => {
      tpHelperRef.current?.clean();
      slHelperRef.current?.clean();
    };
  }, []);

  return (
    <Checkbox
      checked={checked.value}
      onChange={(e) => {
        setChecked(e.currentTarget.checked);
        segment.track(ChartEvent.ChartEventToggleClick, {
          event_name: 'TP / SL',
          action: e.currentTarget.checked ? 'turn_on' : 'turn_off',
        });
      }}
    >
      <FormattedMessage id='Trade.r250401.Tp_sl_label' />
    </Checkbox>
  );
};

// --- Limit Orders ---

export const DraggableLimitOrdersPlugin = ({ callbacks }: DraggableOrdersSectionProps) => {
  const tv = useTvHeaderContext();
  const helperRef = useRef<DraggableOrderLineHelper>(null);
  const [checked, setChecked] = useTradingViewDataStorage({ key: 'limitOrderVisible' });
  const [symbol, setSymbol] = useState('');

  const { pendingPositionsAtom } = useBunja(ordersBunja);
  const pendingPositions = useAtomValue(pendingPositionsAtom);

  useEffect(() => {
    helperRef.current?.clean();
    helperRef.current = new DraggableOrderLineHelper(
      tv,
      (side) => (side === 'Long' ? LineStyles.TriggerLong : LineStyles.TriggerShort),
      callbacks,
    );
    const handler: TvEventHandler<'onFirstDataLoaded'> = ({ symbol: newSymbol, symbolChanged }) => {
      if (!symbolChanged) return;
      helperRef.current?.clean();
      setSymbol(newSymbol);
    };
    return tv.on('onFirstDataLoaded', handler);
  }, [tv, callbacks]);

  const { symbol: symbolWithoutPriceType } = symbolNameToInfo(symbol);
  const limitOrders = pendingPositions?.[symbolWithoutPriceType]?.filter((p) => p.orderType === OrderTypeEnum['Limit']);

  useEffect(() => {
    if (!checked.value || !limitOrders?.length) {
      helperRef.current?.clean();
      return;
    }
    const orders: OrderLike[] = limitOrders.map((o) => ({
      id: o.orderId,
      side: o.side,
      label: 'Limit',
      qty: numberFormatter(o.qty),
      price: Num(o?.price || 0).toNumber(),
      slot: o.slot,
    }));
    helperRef.current?.draw(symbol, orders);
  }, [checked.value, symbol, limitOrders]);

  useEffect(() => () => helperRef.current?.clean(), []);

  return (
    <Checkbox
      checked={checked.value}
      onChange={(e) => {
        setChecked(e.currentTarget.checked);
        segment.track(ChartEvent.ChartEventToggleClick, {
          event_name: 'Limit orders',
          action: e.currentTarget.checked ? 'turn_on' : 'turn_off',
        });
      }}
    >
      <FormattedMessage id='Trade.r260616.Limit_orders' />
    </Checkbox>
  );
};

// --- Positions (with close button) ---

const PositionLineMapper = {
  Long: {
    prefix: 'Long P&L ',
    color: ChartStyles.Color.Up,
  },
  Short: {
    prefix: 'Short P&L ',
    color: ChartStyles.Color.Down,
  },
};

class ClosablePositionLineHelper {
  #lines: Record<string, IOrderLineAdapter> = {};

  constructor(
    private readonly tv: TradingView,
    private readonly onCancelClick: (positionId: string) => void,
  ) {}

  draw(symbol: string, positions: OpenPosition[]) {
    if (this.tv.symbol !== symbol) return;

    // Remove lines for positions that no longer exist
    Object.keys(this.#lines).forEach((id) => {
      if (!positions.find((p) => p.positionId === id)) this.#remove(id);
    });

    positions.forEach((p) => this.#draw(p));
  }

  #draw(position: OpenPosition) {
    try {
      if (!this.tv.enabled || !this.tv.activeChart) return;
      const lineInfo = PositionLineMapper[position.side];
      const entryPrice = Number(position.avgEntryPrice || 0);
      if (entryPrice === 0) return;

      if (!this.#lines[position.positionId]) {
        const positionId = position.positionId;
        const line = this.tv
          .createOrderLine()
          .setLineColor(lineInfo.color)
          .setBodyTextColor(ChartStyles.Color.White)
          .setBodyBackgroundColor(lineInfo.color)
          .setBodyBorderColor(lineInfo.color)
          .setQuantityTextColor(ChartStyles.Color.White)
          .setQuantityBackgroundColor(ChartStyles.Color.Background)
          .setQuantityBorderColor(lineInfo.color)
          .setEditable(false)
          .setCancellable(true)
          .setCancelTooltip('Close position')
          .setCancelButtonBackgroundColor(ChartStyles.Color.Background)
          .setCancelButtonBorderColor(lineInfo.color)
          .setCancelButtonIconColor(ChartStyles.Color.White)
          .setModifyTooltip('');

        line.onCancel(() => {
          this.onCancelClick(positionId);
        });

        this.#lines[position.positionId] = line;
      }

      const pnl = Num(position.unrealizedPnl);
      const pnlStr = `${pnl.isPositive() ? '+' : ''}${numberFormatter(pnl.toFixed(UsdtPrecision))}`;

      this.#lines[position.positionId]
        .setText(lineInfo.prefix + pnlStr)
        .setQuantity(numberFormatter(Num(position.quantity).abs().toString()))
        .setPrice(entryPrice);
    } catch {
      // Value is null error
    }
  }

  #remove(id: string) {
    this.#lines[id]?.remove();
    delete this.#lines[id];
  }

  clean() {
    Object.keys(this.#lines).forEach((id) => this.#remove(id));
  }
}

interface DraggablePositionsPluginProps {
  onPositionClose: (position: OpenPosition) => void;
}

export const DraggablePositionsPlugin = ({ onPositionClose }: DraggablePositionsPluginProps) => {
  const tv = useTvHeaderContext();
  const helperRef = useRef<ClosablePositionLineHelper>(null);
  const [checked, setChecked] = useTradingViewDataStorage({ key: 'positionsVisible' });
  const [symbol, setSymbol] = useState('');

  const { openPositionsAtom } = useBunja(openPositionsBunja);
  const openPositions = useAtomValue(openPositionsAtom);

  // Store positions and callback in refs so the cancel callback always uses latest values
  const positionsRef = useRef<OpenPosition[]>([]);
  const onPositionCloseRef = useRef(onPositionClose);
  onPositionCloseRef.current = onPositionClose;

  useEffect(() => {
    if (!helperRef.current) {
      helperRef.current = new ClosablePositionLineHelper(tv, (positionId: string) => {
        const position = positionsRef.current.find((p) => p.positionId === positionId);
        if (position) onPositionCloseRef.current(position);
      });
    }
    const handler: TvEventHandler<'onFirstDataLoaded'> = ({ symbol: newSymbol, symbolChanged }) => {
      if (!symbolChanged) return;
      helperRef.current?.clean();
      setSymbol(newSymbol);
    };
    return tv.on('onFirstDataLoaded', handler);
  }, [tv, onPositionClose]);

  const { symbol: symbolWithoutPriceType } = symbolNameToInfo(symbol);
  const symbolPositions = openPositions?.[symbolWithoutPriceType];

  useEffect(() => {
    positionsRef.current = symbolPositions || [];
    if (!checked.value || !symbolPositions?.length) {
      helperRef.current?.clean();
      return;
    }
    helperRef.current?.draw(symbol, symbolPositions);
  }, [checked.value, symbol, symbolPositions]);

  useEffect(() => () => helperRef.current?.clean(), []);

  return (
    <Checkbox
      checked={checked.value}
      onChange={(e) => {
        setChecked(e.currentTarget.checked);
        segment.track(ChartEvent.ChartEventToggleClick, {
          event_name: 'Positions',
          action: e.currentTarget.checked ? 'turn_on' : 'turn_off',
        });
      }}
    >
      <FormattedMessage id='Trade.r250401.Positions' />
    </Checkbox>
  );
};
